import { Locale } from './translations';

type Feature = { emoji: string; title: string; body: string };
type Tech = { name: string; label: string };
type ReportItem = { emoji: string; title: string; body: string };

export const publicPagesContent: Record<
  Locale,
  {
    about: {
      title: string;
      subtitle: string;
      missionTitle: string;
      missionParagraphs: string[];
      whyTitle: string;
      features: Feature[];
      privacyTitle: string;
      privacyBullets: string[];
      privacyNotePrefix: string;
      privacyLinkText: string;
      privacyNoteSuffix: string;
      techTitle: string;
      techIntro: string;
      techStack: Tech[];
      contactTitle: string;
      contactBody: string;
      faqCta: string;
      abuseCta: string;
    };
    abuse: {
      title: string;
      subtitle: string;
      whatTitle: string;
      reportItems: ReportItem[];
      nextTitle: string;
      nextBody: string;
      nextBullets: string[];
      privacyLabel: string;
      privacyPrefix: string;
      privacyLinkText: string;
      privacySuffix: string;
    };
  }
> = {
  en: {
    about: {
      title: 'About byt.la',
      subtitle: 'Fast, secure, and privacy-focused URL shortening',
      missionTitle: 'Our Mission',
      missionParagraphs: [
        'byt.la was created to provide a fast, reliable, and privacy-focused URL shortening service that anyone can use.',
        'We believe URL shorteners should be simple, secure, and transparent.',
        "Unlike many URL shorteners that track every click and sell your data, we're committed to protecting your privacy while providing powerful analytics for legitimate use cases.",
      ],
      whyTitle: 'Why Choose byt.la?',
      features: [
        {
          emoji: '🚀',
          title: 'Lightning Fast',
          body: 'Redirects happen in milliseconds, ensuring your users never wait.',
        },
        {
          emoji: '🔒',
          title: 'Secure by Default',
          body: 'We validate URLs to block malicious content and dangerous schemes.',
        },
        {
          emoji: '📊',
          title: 'Powerful Analytics',
          body: 'Track clicks, referrers, devices, and more with bot filtering.',
        },
        {
          emoji: '🎨',
          title: 'Custom Short Codes',
          body: 'Create branded, memorable short links for campaigns.',
        },
        {
          emoji: '📱',
          title: 'QR Code Generation',
          body: 'Generate QR codes in PNG or SVG with customizable sizes.',
        },
        {
          emoji: '🌙',
          title: 'Dark Mode',
          body: 'A comfortable interface for late‑night link creation.',
        },
        {
          emoji: '⏱️',
          title: 'Link Expiration',
          body: 'Set expiration dates for temporary campaigns or content.',
        },
        {
          emoji: '🛡️',
          title: 'Abuse Prevention',
          body: 'CAPTCHA, rate limiting, and reporting keep the service safe.',
        },
      ],
      privacyTitle: 'Our Privacy Commitment',
      privacyBullets: [
        'We never sell your data to third parties',
        'Analytics data is used only to provide insights to link creators',
        "We don't track users across different websites",
        'All passwords are securely hashed with bcrypt',
        'HTTPS encryption protects all data in transit',
      ],
      privacyNotePrefix: 'Read our full',
      privacyLinkText: 'Privacy Policy',
      privacyNoteSuffix: 'for complete details.',
      techTitle: 'Built with Modern Technology',
      techIntro:
        'byt.la is built using modern web technologies to ensure reliability, performance, and security:',
      techStack: [
        { name: 'Next.js 16', label: 'Framework' },
        { name: 'PostgreSQL', label: 'Database' },
        { name: 'Docker', label: 'Deployment' },
        { name: 'TypeScript', label: 'Language' },
      ],
      contactTitle: 'Questions or Feedback?',
      contactBody: "We'd love to hear from you. Check our FAQ or report abuse.",
      faqCta: 'Visit FAQ',
      abuseCta: 'Report Abuse',
    },
    abuse: {
      title: 'Report Abuse',
      subtitle: 'Help us keep byt.la safe by reporting malicious or inappropriate content',
      whatTitle: 'What should I report?',
      reportItems: [
        {
          emoji: '🔗',
          title: 'Phishing links',
          body: 'Sites impersonating legitimate services to steal credentials',
        },
        {
          emoji: '🦠',
          title: 'Malware distribution',
          body: 'Links hosting or distributing malicious software',
        },
        {
          emoji: '💰',
          title: 'Scams',
          body: 'Fraudulent schemes, fake giveaways, or crypto scams',
        },
        { emoji: '📧', title: 'Spam', body: 'Mass unsolicited content or automated abuse' },
        { emoji: '⚖️', title: 'Illegal content', body: 'Content that violates laws' },
        {
          emoji: '👤',
          title: 'Copyright/trademark violations',
          body: 'Unauthorized use of protected content',
        },
      ],
      nextTitle: 'What happens next?',
      nextBody: 'Our team reviews all abuse reports within 24-48 hours. We will:',
      nextBullets: [
        'Investigate the reported link',
        'Take appropriate action (warning, suspension, or removal)',
        'Notify authorities for serious violations',
        'Keep a record to prevent repeat abuse',
      ],
      privacyLabel: 'Privacy Notice:',
      privacyPrefix:
        'Your report will be kept confidential. We may store your IP and details as outlined in our',
      privacyLinkText: 'Privacy Policy',
      privacySuffix: '.',
    },
  },
  th: {
    about: {
      title: 'เกี่ยวกับ byt.la',
      subtitle: 'ย่อลิงก์ที่รวดเร็ว ปลอดภัย และคำนึงถึงความเป็นส่วนตัว',
      missionTitle: 'ภารกิจของเรา',
      missionParagraphs: [
        'byt.la ถูกสร้างขึ้นเพื่อให้บริการย่อลิงก์ที่รวดเร็ว เชื่อถือได้ และเป็นส่วนตัว',
        'เราเชื่อว่าบริการย่อลิงก์ควรเรียบง่าย ปลอดภัย และโปร่งใส',
        'เราให้ความสำคัญกับความเป็นส่วนตัว พร้อมมอบสถิติที่ทรงพลังสำหรับการใช้งานที่เหมาะสม',
      ],
      whyTitle: 'ทำไมต้องเลือก byt.la?',
      features: [
        { emoji: '🚀', title: 'รวดเร็วทันใจ', body: 'เปลี่ยนเส้นทางภายในเสี้ยววินาที' },
        {
          emoji: '🔒',
          title: 'ปลอดภัยเป็นค่าเริ่มต้น',
          body: 'ตรวจสอบลิงก์เพื่อบล็อกเนื้อหาอันตราย',
        },
        { emoji: '📊', title: 'สถิติทรงพลัง', body: 'ติดตามคลิก แหล่งที่มา อุปกรณ์ พร้อมกรองบอต' },
        { emoji: '🎨', title: 'โค้ดสั้นกำหนดเอง', body: 'สร้างลิงก์สั้นแบบแบรนด์ของคุณ' },
        { emoji: '📱', title: 'สร้าง QR ได้ทันที', body: 'ดาวน์โหลด PNG หรือ SVG ได้' },
        { emoji: '🌙', title: 'โหมดมืด', body: 'สบายตาในเวลากลางคืน' },
        { emoji: '⏱️', title: 'ตั้งวันหมดอายุ', body: 'กำหนดหมดอายุสำหรับแคมเปญชั่วคราว' },
        {
          emoji: '🛡️',
          title: 'ป้องกันการละเมิด',
          body: 'ใช้ CAPTCHA และการรายงานเพื่อความปลอดภัย',
        },
      ],
      privacyTitle: 'คำมั่นด้านความเป็นส่วนตัว',
      privacyBullets: [
        'เราไม่ขายข้อมูลของคุณให้บุคคลที่สาม',
        'ข้อมูลสถิติใช้เพื่อให้ข้อมูลแก่ผู้สร้างลิงก์เท่านั้น',
        'เราไม่ติดตามผู้ใช้ข้ามเว็บไซต์',
        'รหัสผ่านถูกแฮชด้วย bcrypt',
        'ข้อมูลส่งผ่าน HTTPS',
      ],
      privacyNotePrefix: 'อ่าน',
      privacyLinkText: 'นโยบายความเป็นส่วนตัว',
      privacyNoteSuffix: 'ฉบับเต็ม',
      techTitle: 'สร้างด้วยเทคโนโลยีสมัยใหม่',
      techIntro: 'byt.la ใช้เทคโนโลยีเว็บสมัยใหม่เพื่อความเสถียรและปลอดภัย:',
      techStack: [
        { name: 'Next.js 16', label: 'เฟรมเวิร์ก' },
        { name: 'PostgreSQL', label: 'ฐานข้อมูล' },
        { name: 'Docker', label: 'การดีพลอย' },
        { name: 'TypeScript', label: 'ภาษา' },
      ],
      contactTitle: 'มีคำถามหรือข้อเสนอแนะ?',
      contactBody: 'ดู FAQ หรือรายงานการละเมิดได้ที่นี่',
      faqCta: 'ไปที่ FAQ',
      abuseCta: 'รายงานการละเมิด',
    },
    abuse: {
      title: 'รายงานการละเมิด',
      subtitle: 'ช่วยให้ byt.la ปลอดภัยโดยรายงานเนื้อหาไม่เหมาะสม',
      whatTitle: 'ควรรายงานอะไรบ้าง?',
      reportItems: [
        { emoji: '🔗', title: 'ฟิชชิง', body: 'เว็บไซต์ปลอมเพื่อขโมยข้อมูล' },
        { emoji: '🦠', title: 'มัลแวร์', body: 'ลิงก์ที่แจกจ่ายซอฟต์แวร์อันตราย' },
        { emoji: '💰', title: 'หลอกลวง', body: 'การฉ้อโกงหรือแจกของปลอม' },
        { emoji: '📧', title: 'สแปม', body: 'การส่งข้อความจำนวนมาก' },
        { emoji: '⚖️', title: 'ผิดกฎหมาย', body: 'เนื้อหาที่ละเมิดกฎหมาย' },
        {
          emoji: '👤',
          title: 'ละเมิดลิขสิทธิ์',
          body: 'ใช้เนื้อหาที่มีลิขสิทธิ์โดยไม่ได้รับอนุญาต',
        },
      ],
      nextTitle: 'ขั้นตอนต่อไป',
      nextBody: 'เราตรวจสอบรายงานภายใน 24-48 ชั่วโมง โดยจะ:',
      nextBullets: [
        'ตรวจสอบลิงก์ที่รายงาน',
        'ดำเนินการที่เหมาะสม (เตือน/ระงับ/ลบ)',
        'แจ้งหน่วยงานที่เกี่ยวข้องหากร้ายแรง',
        'บันทึกเพื่อป้องกันการกระทำซ้ำ',
      ],
      privacyLabel: 'ประกาศความเป็นส่วนตัว:',
      privacyPrefix: 'รายงานของคุณจะถูกเก็บเป็นความลับ ตามนโยบายของเราใน',
      privacyLinkText: 'นโยบายความเป็นส่วนตัว',
      privacySuffix: '',
    },
  },
  id: {
    about: {
      title: 'Tentang byt.la',
      subtitle: 'Pemendek URL yang cepat, aman, dan berfokus pada privasi',
      missionTitle: 'Misi Kami',
      missionParagraphs: [
        'byt.la dibuat untuk menyediakan layanan pemendek URL yang cepat dan andal.',
        'Kami percaya pemendek URL harus sederhana, aman, dan transparan.',
        'Kami menjaga privasi pengguna sambil menyediakan analitik yang bermanfaat.',
      ],
      whyTitle: 'Mengapa memilih byt.la?',
      features: [
        { emoji: '🚀', title: 'Sangat Cepat', body: 'Pengalihan terjadi dalam milidetik.' },
        {
          emoji: '🔒',
          title: 'Aman',
          body: 'Kami memvalidasi URL dan memblokir konten berbahaya.',
        },
        { emoji: '📊', title: 'Analitik Kuat', body: 'Lacak klik, referer, dan perangkat.' },
        { emoji: '🎨', title: 'Kode Pendek Kustom', body: 'Buat tautan bermerek.' },
        { emoji: '📱', title: 'QR Code', body: 'Buat QR dalam PNG atau SVG.' },
        { emoji: '🌙', title: 'Mode Gelap', body: 'Nyaman untuk mata.' },
        { emoji: '⏱️', title: 'Kedaluwarsa Tautan', body: 'Atur masa berlaku tautan.' },
        {
          emoji: '🛡️',
          title: 'Pencegahan Penyalahgunaan',
          body: 'CAPTCHA dan pelaporan komunitas.',
        },
      ],
      privacyTitle: 'Komitmen Privasi Kami',
      privacyBullets: [
        'Kami tidak menjual data Anda',
        'Analitik hanya untuk pemilik tautan',
        'Kami tidak melacak lintas situs',
        'Kata sandi di-hash dengan bcrypt',
        'HTTPS melindungi data',
      ],
      privacyNotePrefix: 'Baca',
      privacyLinkText: 'Kebijakan Privasi',
      privacyNoteSuffix: 'untuk detail lengkap.',
      techTitle: 'Dibangun dengan Teknologi Modern',
      techIntro: 'byt.la menggunakan teknologi modern untuk keamanan dan performa:',
      techStack: [
        { name: 'Next.js 16', label: 'Framework' },
        { name: 'PostgreSQL', label: 'Database' },
        { name: 'Docker', label: 'Deployment' },
        { name: 'TypeScript', label: 'Bahasa' },
      ],
      contactTitle: 'Pertanyaan atau Masukan?',
      contactBody: 'Kunjungi FAQ atau laporkan penyalahgunaan.',
      faqCta: 'Kunjungi FAQ',
      abuseCta: 'Laporkan Penyalahgunaan',
    },
    abuse: {
      title: 'Laporkan Penyalahgunaan',
      subtitle: 'Bantu menjaga byt.la aman dengan melaporkan konten berbahaya',
      whatTitle: 'Apa yang harus dilaporkan?',
      reportItems: [
        { emoji: '🔗', title: 'Phishing', body: 'Situs palsu untuk mencuri kredensial' },
        { emoji: '🦠', title: 'Malware', body: 'Tautan yang menyebarkan malware' },
        { emoji: '💰', title: 'Penipuan', body: 'Skema penipuan atau giveaway palsu' },
        { emoji: '📧', title: 'Spam', body: 'Konten massal tak diinginkan' },
        { emoji: '⚖️', title: 'Konten Ilegal', body: 'Konten yang melanggar hukum' },
        { emoji: '👤', title: 'Hak cipta', body: 'Pelanggaran hak cipta/merek' },
      ],
      nextTitle: 'Langkah selanjutnya',
      nextBody: 'Kami meninjau laporan dalam 24-48 jam dan akan:',
      nextBullets: [
        'Menyelidiki tautan',
        'Mengambil tindakan yang sesuai',
        'Memberi tahu pihak terkait jika perlu',
        'Mencatat untuk mencegah penyalahgunaan ulang',
      ],
      privacyLabel: 'Pemberitahuan Privasi:',
      privacyPrefix: 'Laporan Anda bersifat rahasia. Detail disimpan sesuai',
      privacyLinkText: 'Kebijakan Privasi',
      privacySuffix: '.',
    },
  },
};
